import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Tags, Check, Wand2, FolderInput, Sparkles } from "lucide-react";
import { mailApi } from "./mailApi";

const TRASH = "CESTINO";

// AI-proposed Gmail label per sender; owner confirms per label group or per sender.
export function MailLabels({ onChanged }) {
  const [data, setData] = useState(null);
  const [labels, setLabels] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => Promise.all([mailApi.labelSuggestions(), mailApi.labels()]).then(([d, l]) => { setData(d); setLabels([...new Set([...l.existing, ...l.suggested_new, ...d.rows.map((r) => r.label).filter(Boolean), TRASH])].sort()); }).catch(() => toast.error("Errore caricamento etichette"));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (data?.state?.status !== "running") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data?.state?.status]); // eslint-disable-line

  const classify = async (onlyNew = false) => { setBusy("classify"); try { await mailApi.classify(onlyNew); toast.success(onlyNew ? "Cerco i nuovi mittenti…" : "Smistamento IA avviato"); setTimeout(load, 1500); } catch { toast.error("Errore"); } finally { setBusy(""); } };
  const setLabel = async (email, label) => { await mailApi.setLabelSuggestion(email, label || null); setData((d) => ({ ...d, rows: d.rows.map((r) => (r.email === email ? { ...r, label: label || null } : r)) })); };
  const apply = async (rows, label) => {
    const n = rows.reduce((a, r) => a + r.count, 0);
    const msg = label === TRASH ? `Spostare nel Cestino ${n} mail di ${rows.length} mittente/i e cestinare anche le future?` : `Spostare ${n} mail di ${rows.length} mittente/i in "${label}" e farci arrivare anche le prossime?`;
    if (!window.confirm(msg)) return;
    setBusy(label);
    try {
      const emails = rows.map((r) => r.email);
      if (label === TRASH) await mailApi.sendersAction(emails, "block");
      else await mailApi.sendersAction(emails, "label", label);
      toast.success(`${n} mail smistate in ${label}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(""); }
  };

  const groups = {};
  (data?.rows || []).forEach((r) => { const k = r.label || "— da decidere —"; (groups[k] = groups[k] || []).push(r); });
  const keys = Object.keys(groups).sort((a, b) => (a.startsWith("—") ? 1 : b.startsWith("—") ? -1 : a.localeCompare(b)));
  const applyAll = async () => {
    const todo = keys.filter((k) => !k.startsWith("—") && k !== TRASH);
    const n = todo.reduce((a, k) => a + groups[k].reduce((x, r) => x + r.count, 0), 0);
    if (!window.confirm(`Confermare tutte le ${todo.length} etichette proposte (${n} mail)? Il Cestino e i mittenti "da decidere" restano esclusi.`)) return;
    setBusy("all");
    try {
      for (const k of todo) await mailApi.sendersAction(groups[k].map((r) => r.email), "label", k);
      toast.success(`${n} mail smistate in ${todo.length} etichette`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(""); }
  };

  if (!data) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#4a1c17]" /></div>;
  const st = data.state || {};

  return (
    <div data-testid="mail-labels">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 min-w-[240px] text-xs text-[#7c5a4e]">Ogni mittente nuovo che arriva in Posta in arrivo (senza regola) viene proposto qui con l'etichetta suggerita dall'IA, in automatico a ogni aggiornamento o premendo "Nuovi mittenti". Correggi e conferma: le mail si spostano e le prossime ci finiscono da sole.</p>
        <button onClick={() => classify(true)} disabled={!!busy || st.status === "running"} className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-sm font-semibold disabled:opacity-60" data-testid="mail-labels-new-senders">
          {st.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Nuovi mittenti della settimana
        </button>
        <button onClick={() => classify(false)} disabled={!!busy || st.status === "running"} className="inline-flex items-center gap-1.5 rounded-full border border-[#C48B31] text-[#a8731f] bg-white px-3 py-1.5 text-sm font-semibold disabled:opacity-60" data-testid="mail-labels-classify">
          <Wand2 className="h-4 w-4" /> Ri-smista tutti
        </button>
      </div>
      {st.status === "error" && <p className="mt-2 text-sm text-red-700">Errore: {st.error}</p>}
      {keys.some((k) => !k.startsWith("—") && k !== TRASH) && st.status !== "running" && (
        <button onClick={applyAll} disabled={!!busy} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="mail-labels-apply-all">{busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Conferma tutte le etichette proposte</button>
      )}
      {data.rows.length === 0 && st.status !== "running" && <p className="py-8 text-center text-sm text-[#7c5a4e]" data-testid="mail-labels-empty">Nessun mittente nuovo da smistare: la Posta in arrivo è in ordine.</p>}
      <div className="mt-4 space-y-4">
        {keys.map((k) => {
          const rows = groups[k];
          const undecided = k.startsWith("—");
          return (
            <div key={k} className="rounded-xl border border-[#e8dcc8] bg-white overflow-hidden" data-testid={`mail-label-group-${k}`}>
              <div className="flex items-center gap-2 px-3 py-2 bg-[#faf3ee]">
                <Tags className="h-4 w-4 text-[#4a1c17]" />
                <span className="font-semibold text-sm text-[#2b1410] flex-1">{k} <span className="text-xs text-[#9a7f72] font-normal">· {rows.length} mittenti · {rows.reduce((a, r) => a + r.count, 0)} mail</span></span>
                {!undecided && <button onClick={() => apply(rows, k)} disabled={!!busy} className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] text-[#f7ebdf] px-3 py-1 text-xs font-semibold disabled:opacity-50" data-testid={`mail-label-apply-${k}`}>{busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Conferma tutti</button>}
              </div>
              {rows.map((r) => (
                <div key={r.email} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 border-t border-[#f0e6d8]" data-testid={`mail-label-row-${r.email}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm"><b className="text-[#2b1410]">{r.name || r.email}</b> <span className="text-xs text-[#7c5a4e]">{r.email}</span> <span className="text-xs text-[#9a7f72]">· {r.count} mail</span></div>
                    <div className="text-xs text-[#5b4036] truncate">{r.subject}{r.reason ? <span className="text-[#9a7f72]"> — {r.reason}</span> : null}</div>
                  </div>
                  <select value={r.label || ""} onChange={(e) => setLabel(r.email, e.target.value)} className="rounded-md border border-[#e8dcc8] bg-white px-2 py-1 text-xs max-w-[220px]" data-testid={`mail-label-select-${r.email}`}>
                    <option value="">— da decidere —</option>
                    {labels.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  {r.label && <button onClick={() => apply([r], r.label)} disabled={!!busy} title="Sposta ora" className="p-1.5 rounded-md text-[#4a1c17] hover:bg-[#f7ebdf]" data-testid={`mail-label-apply-one-${r.email}`}><FolderInput className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
